import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import {
  useRealtime, type ExtensionLive, type QueueLive, type RealtimeState,
} from '../../lib/api/realtime.js';

/**
 * The switch's live state, shared by every realtime page.
 *
 * One hook rather than two copies, because the two realtime pages read the same
 * stream: `/v1/live/state` publishes extensions and queues in one payload, and
 * a page that subscribed for only half of it would open a second SSE connection
 * to receive data it already had.
 *
 * Scope is resolved here too. A platform operator watches every tenant on the
 * `*` channel; a tenant user watches their own — the API refuses the platform
 * channel without platform scope, so getting this wrong is a 403 rather than a
 * leak, but it is still the page's job to ask for the right thing.
 */

export interface LiveState {
  extensions: ExtensionLive[];
  queues: QueueLive[];
  connection: RealtimeState;
  /**
   * How many nodes reported recently enough to be believed.
   *
   * The API drops any node whose last push is older than its staleness window
   * and returns the count of the rest. Zero means nothing on the switch side is
   * talking, and every number on the page is a leftover — which is worth saying
   * out loud on a page whose whole claim is that it is live.
   *
   * Undefined until the first snapshot lands, so a page can tell "no nodes"
   * apart from "have not asked yet".
   */
  nodes: number | undefined;
  /**
   * True once the stream has delivered at least one payload.
   *
   * Distinguishes "nothing is happening" from "we have not heard yet", which
   * the two look identical without it — and they mean opposite things when the
   * page is claiming to be live.
   */
  streamed: boolean;
}

export function useLiveState(): LiveState {
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  const platform = session?.scope.all_tenants === true;
  const tenantId = platform ? null : activeTenantId;

  const [calls, setCalls] = useState<{ extensions: ExtensionLive[]; queues: QueueLive[] } | null>(null);

  /**
   * A ref, not state: it must be true for the render that consumes the first
   * payload, and a second setState would render the "not heard yet" branch once
   * before correcting itself.
   */
  const streamed = useRef(false);

  const connection = useRealtime(tenantId, {
    platform,
    onTelephony: (payload) => { streamed.current = true; setCalls(payload); },
  });

  /**
   * The stream carries CHANGES, so a page opened mid-call would show nothing
   * until the next one. This is the state as it stands right now.
   */
  const { data: initial } = useQuery({
    queryKey: ['live-state', tenantId ?? 'platform'],
    queryFn: () => api.get<{ extensions: ExtensionLive[]; queues: QueueLive[]; nodes: number }>(
      tenantId ? `/v1/live/state?tenant_id=${encodeURIComponent(tenantId)}` : '/v1/live/state'),
    enabled: platform || !!tenantId,
    refetchInterval: 30_000,
  });

  /**
   * Once the stream has spoken, it wins — INCLUDING when it says "nothing".
   *
   * The previous version fell back to the polled snapshot whenever both arrays
   * were empty, which is exactly the moment the last call ends: the page would
   * keep showing a finished call, from a snapshot up to thirty seconds old,
   * until the poll caught up. An empty payload is an answer, not an absence.
   */
  const source = streamed.current && calls
    ? calls
    : (initial ?? { extensions: [], queues: [] });

  return {
    extensions: source.extensions,
    queues: source.queues,
    connection,
    streamed: streamed.current,
    nodes: initial?.nodes,
  };
}
